import React, { useEffect, useState } from 'react';
import { db, storage } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BRANCHES_COUNT = 3;
const DEFAULT_BRANCHES = [
  { 
    name: 'AL WAKRAH', 
    address: '', 
    imageUrl: '',
    googleMapsLink: '',
    openingHours: {
      monday: { open: '05:00', close: '23:00', closed: false },
      tuesday: { open: '05:00', close: '23:00', closed: false },
      wednesday: { open: '05:00', close: '23:00', closed: false },
      thursday: { open: '05:00', close: '24:00', closed: false },
      friday: { open: '07:00', close: '24:00', closed: false },
      saturday: { open: '07:00', close: '23:00', closed: false },
      sunday: { open: '05:00', close: '23:00', closed: false }
    }
  },
  { 
    name: 'GEWAN ISLAND', 
    address: '', 
    imageUrl: '',
    googleMapsLink: '',
    openingHours: {
      monday: { open: '09:00', close: '23:00', closed: false },
      tuesday: { open: '09:00', close: '23:00', closed: false },
      wednesday: { open: '09:00', close: '23:00', closed: false },
      thursday: { open: '09:00', close: '23:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '09:00', close: '23:00', closed: false },
      sunday: { open: '09:00', close: '23:00', closed: false }
    }
  },
  { 
    name: 'POPUP STORE', 
    address: '', 
    imageUrl: '',
    googleMapsLink: '',
    openingHours: {
      monday: { open: '', close: '', closed: true },
      tuesday: { open: '', close: '', closed: true },
      wednesday: { open: '', close: '', closed: true },
      thursday: { open: '', close: '', closed: true },
      friday: { open: '', close: '', closed: true },
      saturday: { open: '', close: '', closed: true },
      sunday: { open: '', close: '', closed: true }
    },
    contactInfo: '+974 5000 8852'
  },
];

const DEFAULT_DROPS = [
  { name: '', price: '', origin: '', imageUrl: '' },
];

const AdminHomePageEdit = () => {
  const [branches, setBranches] = useState(DEFAULT_BRANCHES);
  const [drops, setDrops] = useState(DEFAULT_DROPS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [imageUploading, setImageUploading] = useState(Array(BRANCHES_COUNT).fill(false));
  const [dropImageUploading, setDropImageUploading] = useState([false]);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'homepage', 'branchLocations');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (Array.isArray(data.branches)) {
            // Ensure all branches have proper structure with default values
            const normalizedBranches = data.branches.map(branch => ({
              ...branch,
              googleMapsLink: branch.googleMapsLink || '',
              openingHours: branch.openingHours || {
                monday: { open: '09:00', close: '23:00', closed: false },
                tuesday: { open: '09:00', close: '23:00', closed: false },
                wednesday: { open: '09:00', close: '23:00', closed: false },
                thursday: { open: '09:00', close: '23:00', closed: false },
                friday: { open: '09:00', close: '23:00', closed: false },
                saturday: { open: '09:00', close: '23:00', closed: false },
                sunday: { open: '09:00', close: '23:00', closed: false }
              }
            }));
            setBranches(normalizedBranches);
          }
          if (Array.isArray(data.newDrops)) setDrops(data.newDrops);
        }
      } catch (err) {
        setMessage('Error loading branch locations.');
      }
      setLoading(false);
    };
    fetchBranches();
  }, []);

  const handleChange = (idx, key, value) => {
    setBranches(prev => prev.map((b, i) => i === idx ? { ...b, [key]: value } : b));
  };

  const handleHoursChange = (branchIdx, day, field, value) => {
    setBranches(prev => prev.map((b, i) => 
      i === branchIdx ? {
        ...b,
        openingHours: {
          ...b.openingHours,
          [day]: {
            ...b.openingHours[day],
            [field]: value
          }
        }
      } : b
    ));
  };

  const handleClosedToggle = (branchIdx, day) => {
    setBranches(prev => prev.map((b, i) => 
      i === branchIdx ? {
        ...b,
        openingHours: {
          ...b.openingHours,
          [day]: {
            ...b.openingHours[day],
            closed: !b.openingHours[day].closed
          }
        }
      } : b
    ));
  };

  const handleImageUpload = async (idx, file) => {
    if (!file) return;
    setImageUploading(prev => prev.map((v, i) => i === idx ? true : v));
    try {
      const storageRef = ref(storage, `branchImages/branch_${idx}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setBranches(prev => prev.map((b, i) => i === idx ? { ...b, imageUrl: url } : b));
    } catch (err) {
      setMessage('Image upload failed.');
    }
    setImageUploading(prev => prev.map((v, i) => i === idx ? false : v));
  };

  const handleDropChange = (idx, key, value) => {
    setDrops(prev => prev.map((d, i) => i === idx ? { ...d, [key]: value } : d));
  };

  const handleDropImageUpload = async (idx, file) => {
    if (!file) return;
    setDropImageUploading(prev => {
      const arr = [...prev];
      arr[idx] = true;
      return arr;
    });
    try {
      const storageRef = ref(storage, `newDrops/drop_${idx}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setDrops(prev => prev.map((d, i) => i === idx ? { ...d, imageUrl: url } : d));
    } catch (err) {
      setMessage('Image upload failed.');
    }
    setDropImageUploading(prev => {
      const arr = [...prev];
      arr[idx] = false;
      return arr;
    });
  };

  const handleAddDrop = () => {
    setDrops(prev => [...prev, { name: '', price: '', origin: '', imageUrl: '' }]);
    setDropImageUploading(prev => [...prev, false]);
  };

  const handleRemoveDrop = (idx) => {
    setDrops(prev => prev.filter((_, i) => i !== idx));
    setDropImageUploading(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveBranch = (idx) => {
    if (branches.length <= 1) {
      setMessage('Cannot remove the last branch. At least one branch is required.');
      return;
    }
    if (window.confirm('Are you sure you want to remove this branch? This action cannot be undone.')) {
      setBranches(prev => prev.filter((_, i) => i !== idx));
      setImageUploading(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const handleAddBranch = () => {
    const newBranch = {
      name: '',
      address: '',
      imageUrl: '',
      googleMapsLink: '',
      openingHours: {
        monday: { open: '09:00', close: '23:00', closed: false },
        tuesday: { open: '09:00', close: '23:00', closed: false },
        wednesday: { open: '09:00', close: '23:00', closed: false },
        thursday: { open: '09:00', close: '23:00', closed: false },
        friday: { open: '09:00', close: '23:00', closed: false },
        saturday: { open: '09:00', close: '23:00', closed: false },
        sunday: { open: '09:00', close: '23:00', closed: false }
      }
    };
    setBranches(prev => [...prev, newBranch]);
    setImageUploading(prev => [...prev, false]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const docRef = doc(db, 'homepage', 'branchLocations');
      await setDoc(docRef, { branches, newDrops: drops });
      setMessage('Branch locations and new drops updated!');
    } catch (err) {
      setMessage('Error saving branch locations.');
    }
    setSaving(false);
  };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Header />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 0' }}>
        <h2 style={{ textAlign: 'center', fontWeight: 900, fontSize: 32, marginBottom: 32 }}>HomePage Edit: Branch Locations</h2>
        <form onSubmit={handleSave} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 18px rgba(0,0,0,0.08)', padding: 32 }}>
          {branches.map((branch, idx) => (
            <div key={idx} style={{ marginBottom: 38, borderBottom: idx < branches.length-1 ? '1px solid #eee' : 'none', paddingBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontWeight: 800, fontSize: 22, margin: 0 }}>Branch {idx+1}</h3>
                <button
                  type="button"
                  onClick={() => handleRemoveBranch(idx)}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 16px',
                    fontSize: 14,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Remove Branch
                </button>
              </div>
              <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 6 }}>Name</label>
              <input
                type="text"
                value={branch.name}
                onChange={e => handleChange(idx, 'name', e.target.value)}
                style={{ width: '100%', fontSize: 16, padding: 10, borderRadius: 6, border: '1px solid #ccc', marginBottom: 14 }}
                required
              />
              <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 6 }}>Location/Address</label>
              <input
                type="text"
                value={branch.address}
                onChange={e => handleChange(idx, 'address', e.target.value)}
                style={{ width: '100%', fontSize: 16, padding: 10, borderRadius: 6, border: '1px solid #ccc', marginBottom: 14 }}
                required
              />
              
              <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 6 }}>Google Maps Link</label>
              <input
                type="url"
                value={branch.googleMapsLink || ''}
                onChange={e => handleChange(idx, 'googleMapsLink', e.target.value)}
                style={{ width: '100%', fontSize: 16, padding: 10, borderRadius: 6, border: '1px solid #ccc', marginBottom: 14 }}
                placeholder="https://maps.google.com/... (Optional - will use address if not provided)"
              />
              
              {/* Contact Info for Popup Store */}
              {branch.name.toLowerCase().includes('popup') && (
                <>
                  <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 6 }}>Contact Info</label>
                  <input
                    type="text"
                    value={branch.contactInfo || ''}
                    onChange={e => handleChange(idx, 'contactInfo', e.target.value)}
                    style={{ width: '100%', fontSize: 16, padding: 10, borderRadius: 6, border: '1px solid #ccc', marginBottom: 14 }}
                    placeholder="Phone number or contact details"
                  />
                </>
              )}

              {/* Opening Hours */}
              <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 10 }}>Opening Hours</label>
              <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 14 }}>
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                  const dayHours = branch.openingHours?.[day] || { open: '09:00', close: '23:00', closed: false };
                  return (
                    <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <div style={{ minWidth: 90, fontWeight: 600, textTransform: 'capitalize' }}>{day}</div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={dayHours.closed || false}
                          onChange={() => handleClosedToggle(idx, day)}
                        />
                        Closed
                      </label>
                      {!dayHours.closed && (
                        <>
                          <input
                            type="time"
                            value={dayHours.open || ''}
                            onChange={e => handleHoursChange(idx, day, 'open', e.target.value)}
                            style={{ padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={dayHours.close === '24:00' ? '23:59' : dayHours.close || ''}
                            onChange={e => {
                              const value = e.target.value === '23:59' ? '24:00' : e.target.value;
                              handleHoursChange(idx, day, 'close', value);
                            }}
                            style={{ padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
                          />
                          {dayHours.close === '24:00' && (
                            <span style={{ fontSize: 12, color: '#666' }}>(Midnight)</span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <label style={{ fontWeight: 700, fontSize: 16, display: 'block', marginBottom: 6 }}>Image</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 10 }}>
                {branch.imageUrl && <img src={branch.imageUrl} alt="branch" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />}
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleImageUpload(idx, e.target.files[0])}
                  disabled={imageUploading[idx]}
                />
                {imageUploading[idx] && <span style={{ color: '#888', fontSize: 15 }}>Uploading...</span>}
              </div>
            </div>
          ))}
          
          {/* Add Branch Button */}
          <div style={{ marginTop: 30, marginBottom: 20, textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleAddBranch}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '12px 24px',
                fontSize: 16,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Add New Branch
            </button>
          </div>
          
          <div style={{ marginTop: 48, marginBottom: 18 }}>
            <h2 style={{ fontWeight: 900, fontSize: 28, marginBottom: 18 }}>New Drops</h2>
            {drops.map((drop, idx) => (
              <div key={idx} style={{ marginBottom: 32, borderBottom: '1px solid #eee', paddingBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <h3 style={{ fontWeight: 800, fontSize: 19, margin: 0 }}>Drop {idx+1}</h3>
                  <button type="button" onClick={() => handleRemoveDrop(idx)} style={{ background: 'none', border: 'none', color: '#c00', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Remove</button>
                </div>
                <label style={{ fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 4 }}>Name</label>
                <input
                  type="text"
                  value={drop.name}
                  onChange={e => handleDropChange(idx, 'name', e.target.value)}
                  style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 6, border: '1px solid #ccc', marginBottom: 10 }}
                  required
                />
                <label style={{ fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 4 }}>Price</label>
                <input
                  type="text"
                  value={drop.price}
                  onChange={e => handleDropChange(idx, 'price', e.target.value)}
                  style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 6, border: '1px solid #ccc', marginBottom: 10 }}
                  required
                />
                <label style={{ fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 4 }}>Origin</label>
                <input
                  type="text"
                  value={drop.origin}
                  onChange={e => handleDropChange(idx, 'origin', e.target.value)}
                  style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 6, border: '1px solid #ccc', marginBottom: 10 }}
                  required
                />
                <label style={{ fontWeight: 700, fontSize: 15, display: 'block', marginBottom: 4 }}>Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 10 }}>
                  {drop.imageUrl && <img src={drop.imageUrl} alt="drop" style={{ width: 90, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => handleDropImageUpload(idx, e.target.files[0])}
                    disabled={dropImageUploading[idx]}
                  />
                  {dropImageUploading[idx] && <span style={{ color: '#888', fontSize: 14 }}>Uploading...</span>}
                </div>
              </div>
            ))}
            <button type="button" onClick={handleAddDrop} style={{ padding: '8px 24px', fontSize: 16, borderRadius: 8, background: '#ededed', color: '#222', border: 'none', cursor: 'pointer', fontWeight: 700, marginTop: 8 }}>Add Drop</button>
          </div>
          <button type="submit" disabled={saving || loading} style={{ padding: '12px 38px', fontSize: 18, borderRadius: 8, background: '#222', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
            {saving ? 'Saving...' : 'Save All'}
          </button>
          {message && <div style={{ marginTop: 18, color: message.includes('updated') ? 'green' : 'red', fontWeight: 600 }}>{message}</div>}
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default AdminHomePageEdit; 